import type { PropsWithChildren } from "react";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BottomNav } from "@/components/layout/BottomNav";
import { DesktopSidebar } from "@/components/layout/DesktopSidebar";
import { AddTaskSheet } from "@/components/sheets/AddTaskSheet";
import { AddAgentSheet } from "@/components/sheets/AddAgentSheet";
import { ClarifySheet } from "@/components/sheets/ClarifySheet";
import { EditTaskSheet } from "@/components/sheets/EditTaskSheet";
import { ProjectProfileSheet } from "@/components/sheets/ProjectProfileSheet";
import { ProjectSetupSheet } from "@/components/sheets/ProjectSetupSheet";
import { useAppStore } from "@/state/appStore";

const hideNavPaths = ["/", "/auth", "/onboarding/workspace", "/onboarding/wizard"];

export const AppShell = ({ children }: PropsWithChildren): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTaskId = useAppStore((state) => state.activeTaskId);

  const sheet = useMemo(() => new URLSearchParams(location.search).get("sheet"), [location.search]);
  const projectIdFromQuery = useMemo(() => new URLSearchParams(location.search).get("projectId") ?? undefined, [location.search]);
  const routeTaskId = useMemo(() => {
    const match = location.pathname.match(/^\/tasks\/([^/]+)$/);
    return match?.[1];
  }, [location.pathname]);
  const showNav = !hideNavPaths.includes(location.pathname);

  const closeSheet = () => {
    navigate(location.pathname, { replace: true });
  };

  return (
    <div className="app-shell">
      {showNav ? <DesktopSidebar /> : null}
      <div className="mobile-frame app-content">{children}</div>
      {showNav ? <BottomNav /> : null}
      <AddAgentSheet open={sheet === "add-agent"} onClose={closeSheet} />
      <AddTaskSheet open={sheet === "add-task"} onClose={closeSheet} />
      <ClarifySheet open={sheet === "clarify"} onClose={closeSheet} taskId={activeTaskId ?? routeTaskId} />
      <EditTaskSheet open={sheet === "edit-task"} onClose={closeSheet} taskId={activeTaskId ?? routeTaskId} />
      <ProjectSetupSheet open={sheet === "project-setup"} onClose={closeSheet} />
      <ProjectProfileSheet open={sheet === "project-profile"} onClose={closeSheet} projectId={projectIdFromQuery} />
    </div>
  );
};
